import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import he from 'date-fns/locale/he';
import arSA from 'date-fns/locale/ar-SA';
import enUS from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { beautifyNumber, roundVatToInteger } from '../../../Utils/FormatNumber';
import { parseSmartDateInput } from '../../../Utils/parseSmartDateInput';
import {
    FOLLOW_UP_STATUS_OPTIONS,
    PAYMENT_METHOD_OPTIONS,
    dateStringToDate,
    dateToDateString,
} from '../reportingFollowUpConstants';
import styles from '../ReportingFollowUp.module.scss';

const DATE_ADAPTER_LOCALES = { he, ar: arSA };

/** Same DatePicker setup as EditModal (dd/MM/yyyy, Enter commits smart-typed dates); value is `YYYY-MM-DD`. */
export const FollowUpDatePicker = ({ value, onChange, disabled = false, ariaLabel, className = '' }) => {
    const { i18n } = useTranslation();
    const adapterLocale = DATE_ADAPTER_LOCALES[i18n.language] || enUS;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
            <DatePicker
                value={dateStringToDate(value)}
                onChange={(d) => {
                    if (d === null) onChange('');
                    else if (!Number.isNaN(d.getTime())) onChange(dateToDateString(d));
                }}
                format="dd/MM/yyyy"
                disabled={disabled}
                slotProps={{
                    textField: {
                        variant: 'standard',
                        fullWidth: true,
                        className: `edit-style-input ${styles.datePicker} ${className}`,
                        InputProps: { disableUnderline: true },
                        inputProps: { 'aria-label': ariaLabel },
                        onKeyDown: (e) => {
                            if (e.key !== 'Enter' || e.shiftKey) return;
                            e.preventDefault();
                            const parsed = parseSmartDateInput(e.target?.value);
                            if (parsed) onChange(dateToDateString(parsed));
                        },
                    },
                    field: { clearable: true },
                    popper: {
                        sx: { zIndex: 2100 },
                    },
                }}
            />
        </LocalizationProvider>
    );
};

FollowUpDatePicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
};

export const formatAmount = (value) => beautifyNumber(roundVatToInteger(value || 0), 0, true);

const NUMERIC_INPUT = /^-?[\d,]*\.?\d*$/;

export const SectionCard = ({ title, fileNumber, reportingType, children }) => {
    const { t } = useTranslation();
    return (
        <section className={styles.card}>
            <header className={styles.cardHeader}>
                <span className={styles.cardTitleGroup}>
                    <span className={styles.cardTitle}>{title}</span>
                    {reportingType ? (
                        <span className={styles.reportingTypeBadge} title={t('ReportingType')}>{reportingType}</span>
                    ) : null}
                </span>
                {fileNumber ? (
                    <span className={styles.fileNumber}>
                        {t('FollowUpFileNumber')}: {fileNumber}
                    </span>
                ) : null}
            </header>
            <div className={styles.cardBody}>{children}</div>
        </section>
    );
};

SectionCard.propTypes = {
    title: PropTypes.node.isRequired,
    fileNumber: PropTypes.string,
    reportingType: PropTypes.string,
    children: PropTypes.node,
};

/** Grid with a label column and `columns.length` value columns; the first header cell shows the period. */
export const ValuesTable = ({ periodLabel, columns, children }) => (
    <div className={styles.table} style={{ '--value-columns': columns.length }}>
        <div className={`${styles.columnHead} ${styles.periodHead}`} title={periodLabel}>{periodLabel}</div>
        {columns.map((column) => (
            <div key={column} className={styles.columnHead} title={column}>{column}</div>
        ))}
        {children}
    </div>
);

ValuesTable.propTypes = {
    periodLabel: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    children: PropTypes.node,
};

export const RowLabel = ({ children }) => <div className={styles.rowLabel}>{children}</div>;

RowLabel.propTypes = { children: PropTypes.node };

const cellClass = (base, { total, mismatch }) =>
    [base, total ? styles.total : '', mismatch ? styles.mismatch : ''].filter(Boolean).join(' ');

/** `mismatch` marks a reported value that differs from the books. */
export const ReadonlyAmount = ({ value, total = false, suffix = '', mismatch = false }) => {
    const { t } = useTranslation();
    return (
        <div
            className={cellClass(styles.readonly, { total, mismatch })}
            title={mismatch ? t('FollowUpReportDiffersFromBooks') : undefined}
        >
            {value === null || value === undefined ? '—' : `${formatAmount(value)}${suffix}`}
        </div>
    );
};

ReadonlyAmount.propTypes = {
    value: PropTypes.number,
    total: PropTypes.bool,
    suffix: PropTypes.string,
    mismatch: PropTypes.bool,
};

export const ReadonlyText = ({ value }) => (
    <div className={styles.readonly}>{value === null || value === undefined || value === '' ? '—' : value}</div>
);

ReadonlyText.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

export const EmptyCell = () => <div className={styles.empty} aria-hidden="true" />;

export const AmountInput = ({
    value, onChange, total = false, disabled = false, ariaLabel, mismatch = false, className = '', title,
}) => {
    const { t } = useTranslation();
    return (
        <input
            type="text"
            inputMode="decimal"
            dir="ltr"
            className={`${cellClass(styles.input, { total, mismatch })} ${className}`.trim()}
            value={value ?? ''}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={mismatch || undefined}
            title={mismatch ? t('FollowUpReportDiffersFromBooks') : title}
            onChange={(event) => {
                const next = event.target.value;
                if (NUMERIC_INPUT.test(next)) onChange(next);
            }}
        />
    );
};

AmountInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    total: PropTypes.bool,
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string,
    mismatch: PropTypes.bool,
    className: PropTypes.string,
    title: PropTypes.string,
};

/** "Number of businesses in the report" row with the Copy (books → report) button. */
export const BusinessesRow = ({ count, onCopy }) => {
    const { t } = useTranslation();
    return (
        <>
            <RowLabel>{t('FollowUpBusinessesInReport')}</RowLabel>
            <span className={styles.businessesCount}>{count}</span>
            {onCopy ? (
                <Button
                    className={styles.copyButton}
                    variant="contained"
                    disableElevation
                    onClick={onCopy}
                    title={t('FollowUpCopyBooksToReport')}
                >
                    {t('FollowUpCopy')}
                </Button>
            ) : <span />}
        </>
    );
};

BusinessesRow.propTypes = {
    count: PropTypes.number,
    onCopy: PropTypes.func,
};

/** Reported / not reported tag, with a button that reports the period while it is still unreported. */
export const ReportedStatus = ({ isReported, onReport, disabled = false }) => {
    const { t } = useTranslation();
    return (
        <div className={styles.statusBar}>
            <span className={isReported ? styles.reportedTag : styles.unreportedTag}>
                {isReported ? t('FollowUpReported') : t('FollowUpNotReported')}
            </span>
            {!isReported && onReport ? (
                <Button
                    className={styles.reportButton}
                    variant="outlined"
                    onClick={onReport}
                    disabled={disabled}
                    title={t('FollowUpMarkReportedHint')}
                >
                    {t('FollowUpMarkReported')}
                </Button>
            ) : null}
        </div>
    );
};

ReportedStatus.propTypes = {
    isReported: PropTypes.bool,
    onReport: PropTypes.func,
    disabled: PropTypes.bool,
};

/** Businesses reported together under the section's file number; hidden for a single business. */
export const GroupMembers = ({ members }) => {
    const { t } = useTranslation();
    if (!members || members.length < 2) return null;
    return (
        <div className={styles.groupMembers}>
            <span className={styles.groupMembersTitle}>{t('FollowUpReportedTogether')}</span>
            <div className={styles.groupMembersList}>
                {members.map((member) => (
                    <span key={member.caseId} className={styles.groupMember} title={member.name || member.caseId}>
                        {member.name ? `${member.name} · ` : ''}{member.caseId}
                    </span>
                ))}
            </div>
        </div>
    );
};

GroupMembers.propTypes = {
    members: PropTypes.arrayOf(PropTypes.shape({
        caseId: PropTypes.string.isRequired,
        name: PropTypes.string,
    })),
};

/** Reporting date, payment method, follow-up status and note — shared by all five sections. */
export const TrackingFields = ({ tracking, onChange, direction }) => {
    const { t } = useTranslation();
    const paymentOptions = PAYMENT_METHOD_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));
    const statusOptions = FOLLOW_UP_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));

    return (
        <>
            <div className={styles.divider} />
            <div className={styles.tracking}>
                <RowLabel>{t('FollowUpReportDate')}</RowLabel>
                <FollowUpDatePicker
                    value={tracking.reportDate}
                    onChange={(value) => onChange('reportDate', value)}
                    ariaLabel={t('FollowUpReportDate')}
                />

                <RowLabel>{t('FollowUpPaymentDate')}</RowLabel>
                <FollowUpDatePicker
                    value={tracking.paymentDate}
                    onChange={(value) => onChange('paymentDate', value)}
                    ariaLabel={t('FollowUpPaymentDate')}
                />

                <RowLabel>{t('FollowUpPaymentMethod')}</RowLabel>
                <AutocompleteSelectFilter
                    options={paymentOptions}
                    selectedValues={paymentOptions.find((o) => o.value === tracking.paymentMethod) || paymentOptions[0]}
                    onChange={(e, option) => onChange('paymentMethod', option?.value ?? 0)}
                    placeholder={t('FollowUpPaymentMethod')}
                    isMultiple={false}
                    direction={direction}
                    portalMenu
                />

                <RowLabel>{t('FollowUpStatus')}</RowLabel>
                <AutocompleteSelectFilter
                    options={statusOptions}
                    selectedValues={statusOptions.find((o) => o.value === tracking.followUpStatus) || statusOptions[0]}
                    onChange={(e, option) => onChange('followUpStatus', option?.value ?? 0)}
                    placeholder={t('FollowUpStatus')}
                    isMultiple={false}
                    direction={direction}
                    portalMenu
                />

                <RowLabel>{t('FollowUpReportNote')}</RowLabel>
                <input
                    type="text"
                    className={`${styles.input} ${styles.textInput}`}
                    value={tracking.note || ''}
                    maxLength={2000}
                    onChange={(event) => onChange('note', event.target.value)}
                />
            </div>
        </>
    );
};

TrackingFields.propTypes = {
    tracking: PropTypes.shape({
        reportDate: PropTypes.string,
        paymentMethod: PropTypes.number,
        followUpStatus: PropTypes.number,
        note: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    direction: PropTypes.string,
};
